interface WikipediaResponse {
  query?: {
    pages?: {
      [key: string]: {
        pageid: number;
        title: string;
        extract?: string;
        fullurl?: string;
      };
    };
    search?: Array<{
      pageid: number;
      title: string;
      snippet: string;
    }>;
  };
}

interface WikipediaImageUrlResponse {
  query: {
    pages: {
      [key: string]: {
        title: string;
        imageinfo: Array<{
          url: string;
        }>;
      };
    };
  };
}

const isAnimalCategory = (category: string): boolean => {
  const animalCategories = [
    'Category:Animals',
    'Category:Fauna',
    'Category:Wildlife',
    'Category:Vertebrates',
    'Category:Invertebrates',
    'Category:Mammals',
    'Category:Birds',
    'Category:Reptiles',
    'Category:Amphibians',
    'Category:Fish',
    'Category:Insects',
    'Category:Arthropods',
    'Category:Endangered species',
    'Category:Critically endangered species',
    'Category:Vulnerable species',
    'Category:Extinct species'
  ];
  
  return animalCategories.some(animalCategory => 
    category.toLowerCase().includes(animalCategory.toLowerCase())
  );
};

const isValidAnimalImage = (imageTitle: string): boolean => {
  const validExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
  const invalidTerms = [
    'map', 'distribution', 'range', 'habitat', 'skeleton', 'skull', 'bone',
    'fossil', 'diagram', 'chart', 'graph', 'illustration', 'drawing', 'painting',
    'logo', 'icon', 'symbol', 'flag', 'coat of arms', 'emblem'
  ];

  // Check if it's a valid image file
  if (!validExtensions.some(ext => imageTitle.toLowerCase().endsWith(ext))) {
    return false;
  }

  // Check if it contains invalid terms
  return !invalidTerms.some(term => 
    imageTitle.toLowerCase().includes(term.toLowerCase())
  );
};

export const getSpeciesImage = async (speciesName: string): Promise<string | null> => {
  try {
    console.log('Starting image fetch for species:', speciesName);
    
    // First, search for the species page
    const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(speciesName)}&format=json&origin=*`;
    console.log('Searching Wikipedia with URL:', searchUrl);
    
    const searchResponse = await fetch(searchUrl, {
      mode: 'cors',
      headers: {
        'Accept': 'application/json'
      }
    });
    if (!searchResponse.ok) {
      console.error('Search request failed:', searchResponse.status, searchResponse.statusText);
      return null;
    }
    const searchData = await searchResponse.json();
    console.log('Search results:', {
      found: searchData.query?.search?.length > 0,
      firstResult: searchData.query?.search?.[0],
      totalResults: searchData.query?.search?.length
    });

    if (!searchData.query?.search?.[0]?.pageid) {
      console.log('No species page found in search results');
      return null;
    }

    const pageId = searchData.query.search[0].pageid;
    console.log('Found page ID:', pageId);

    // Get the page images and categories
    const imagesUrl = `https://en.wikipedia.org/w/api.php?action=query&pageids=${pageId}&prop=images|categories&format=json&origin=*`;
    console.log('Fetching page details from URL:', imagesUrl);
    
    const imagesResponse = await fetch(imagesUrl, {
      mode: 'cors',
      headers: {
        'Accept': 'application/json'
      }
    });
    if (!imagesResponse.ok) {
      console.error('Images request failed:', imagesResponse.status, imagesResponse.statusText);
      return null;
    }
    const imagesData: WikipediaResponse = await imagesResponse.json();
    console.log('Page details:', {
      hasImages: imagesData.query?.pages?.[pageId]?.images?.length > 0,
      imageCount: imagesData.query?.pages?.[pageId]?.images?.length,
      hasCategories: imagesData.query?.pages?.[pageId]?.categories?.length > 0,
      categoryCount: imagesData.query?.pages?.[pageId]?.categories?.length
    });

    const page = imagesData.query.pages[pageId];
    if (!page?.images?.length) {
      console.log('No images found for species page');
      return null;
    }

    // Get image URLs
    const imageTitles = page.images.map(img => img.title);
    console.log('Found image titles:', imageTitles);
    
    const imageUrlUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${imageTitles.join('|')}&prop=imageinfo&iiprop=url&format=json&origin=*`;
    console.log('Fetching image URLs from:', imageUrlUrl);
    
    const imageUrlResponse = await fetch(imageUrlUrl, {
      mode: 'cors',
      headers: {
        'Accept': 'application/json'
      }
    });
    if (!imageUrlResponse.ok) {
      console.error('Image URL request failed:', imageUrlResponse.status, imageUrlResponse.statusText);
      return null;
    }
    const imageUrlData: WikipediaImageUrlResponse = await imageUrlResponse.json();
    console.log('Image URL response:', {
      hasPages: Object.keys(imageUrlData.query?.pages || {}).length > 0,
      pageCount: Object.keys(imageUrlData.query?.pages || {}).length
    });

    // Validate categories to ensure it's an animal
    const categories = page.categories || [];
    console.log('Page categories:', categories.map(cat => cat.title));
    
    const isAnimal = categories.some(cat => isAnimalCategory(cat.title));
    console.log('Is animal page:', isAnimal);
    
    if (!isAnimal) {
      console.log('Page is not categorized as an animal');
      return null;
    }

    // Find a valid animal image
    const validImages = Object.values(imageUrlData.query.pages)
      .filter(page => page.imageinfo?.[0]?.url)
      .map(page => ({
        title: page.title,
        url: page.imageinfo[0].url
      }))
      .filter(img => isValidAnimalImage(img.title));

    console.log('Valid images found:', {
      count: validImages.length,
      titles: validImages.map(img => img.title)
    });

    if (!validImages.length) {
      console.log('No valid animal images found after filtering');
      return null;
    }

    // Return the first valid image
    const imageUrl = validImages[0].url;
    console.log('Selected image URL:', imageUrl);
    return imageUrl;
  } catch (error) {
    console.error('Error in getSpeciesImage:', error);
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
    }
    return null;
  }
};

export const getSpeciesDescription = async (
  commonName: string,
  scientificName?: string
): Promise<{ description?: string; url?: string }> => {
  try {
    // Prioritize scientific name for more accurate results
    const searchTerm = scientificName || commonName;
    console.log('Fetching Wikipedia description for:', { searchTerm });

    // First search for the exact page
    const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchTerm)}&format=json&origin=*`;
    console.log('Searching Wikipedia with URL:', searchUrl);
    
    const searchResponse = await fetch(searchUrl);
    if (!searchResponse.ok) {
      throw new Error(`Wikipedia search failed: ${searchResponse.status}`);
    }

    const searchData: WikipediaResponse = await searchResponse.json();
    const firstResult = searchData.query?.search?.[0];

    if (!firstResult) {
      console.log('No Wikipedia page found');
      return {};
    }

    // Get the page extract (description)
    const extractUrl = `https://en.wikipedia.org/w/api.php?action=query&pageids=${firstResult.pageid}&prop=extracts|info&exintro=true&explaintext=true&inprop=url&format=json&origin=*`;
    console.log('Fetching extract from URL:', extractUrl);
    
    const extractResponse = await fetch(extractUrl);
    if (!extractResponse.ok) {
      throw new Error(`Wikipedia extract fetch failed: ${extractResponse.status}`);
    }

    const extractData: WikipediaResponse = await extractResponse.json();
    const page = extractData.query?.pages?.[firstResult.pageid];

    if (!page) {
      console.log('No page data found');
      return {};
    }

    return {
      description: page.extract,
      url: page.fullurl
    };
  } catch (error) {
    console.error('Error fetching Wikipedia description:', error);
    return {};
  }
}; 